import { useMemo } from "react";
import useUserPermissions from "./useUserPermissions";
import useShortCode from "./useShortCode";
import { hasRole, getRoles } from "../components/FeatureGate/RoleGate";
import { roles, AVAILABLE_ROLES, GOVERNANCE_ROLES } from "../util/constants";

function useCurrentUserRoles() {
  const userPermissions = useUserPermissions();
  const shortCode = useShortCode();

  const currentUserRoles = useMemo(() => {
    if (!userPermissions?.permissions) return [];

    const { organisationRoles = [] } = getRoles(
      { permissions: userPermissions.permissions },
      shortCode,
    );
    return organisationRoles.filter((role) => AVAILABLE_ROLES.includes(role));
  }, [userPermissions?.permissions, shortCode]);

  const isStaff = useMemo(() => {
    const globalRoles = userPermissions?.permissions?.global;
    return Array.isArray(globalRoles) && globalRoles.includes(roles.admin);
  }, [userPermissions?.permissions?.global]);

  const roleChecks = useMemo(
    () => ({
      isOwner: hasRole(userPermissions, roles.owner, shortCode),
      isManager: hasRole(
        userPermissions,
        [roles.manager, roles.cashflowManager],
        shortCode,
      ),
      isContributor: hasRole(
        userPermissions,
        roles.receivablesContributor,
        shortCode,
      ),
      isViewer: hasRole(userPermissions, roles.receivablesViewer, shortCode),
      isBilling: hasRole(userPermissions, roles.billing, shortCode),
    }),
    [userPermissions, shortCode],
  );

  const primaryRole = useMemo(() => {
    if (roleChecks.isOwner) return roles.owner;
    if (roleChecks.isManager) return roles.manager;
    if (roleChecks.isContributor) return roles.receivablesContributor;
    if (roleChecks.isViewer) return roles.receivablesViewer;
    return null;
  }, [roleChecks]);

  const canManageUsers = useMemo(() => {
    return isStaff || roleChecks.isOwner || roleChecks.isManager;
  }, [isStaff, roleChecks]);

  const canManageBilling = useMemo(() => {
    return roleChecks.isOwner;
  }, [roleChecks]);

  const canCreateOrganisation = useMemo(() => {
    if (isStaff) return true;
    const orgEntries = userPermissions?.permissions?.organisation;
    if (!orgEntries || orgEntries.length === 0) return true;
    return orgEntries.some((org) =>
      org.roles?.some((role) => role === roles.owner || role === roles.manager),
    );
  }, [isStaff, userPermissions?.permissions?.organisation]);

  const disabledRoles = useMemo(() => {
    if (isStaff || roleChecks.isOwner) return [];
    if (roleChecks.isManager) return GOVERNANCE_ROLES;
    return AVAILABLE_ROLES;
  }, [isStaff, roleChecks.isOwner, roleChecks.isManager]);

  return {
    roles: currentUserRoles,
    primaryRole,
    disabledRoles,
    canManageUsers,
    canManageBilling,
    canCreateOrganisation,
    isStaff,
    ...roleChecks,
  };
}

export default useCurrentUserRoles;
